import { useCallback, useMemo } from 'react';
import { useSecurityScope } from '@dealstack/frontend/shared/components/FieldBinding/useSecurityScope.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useFieldBindingState } from '@dealstack/frontend/shared/components/FieldBinding/useFieldBindingState.ts';
import { useFieldBindingsQuery } from '../../api/useFieldBindingsQuery.ts';
import { useDataPointCatalogueQuery } from '../../api/useDataPointCatalogueQuery.ts';
import { useUpdateFieldBindings } from '../../api/useUpdateFieldBindings.ts';
import type { FieldBindingDto } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';
import type { FieldBindingResult } from './useFieldBinding.types.ts';

export const useFieldBinding = (
	templateId: string | null,
	previewInvestorId: string | null
): FieldBindingResult => {
	const tMipManage = useTranslationMipManage();
	const { data: bindingsData, isLoading: isLoadingBindings } = useFieldBindingsQuery(templateId);
	const { data: catalogueData, isLoading: isLoadingCatalogue } = useDataPointCatalogueQuery(
		templateId,
		previewInvestorId,
		!!previewInvestorId
	);
	const updateFieldBindings = useUpdateFieldBindings();

	const state = useFieldBindingState({
		serverBindings: bindingsData,
		catalogue: catalogueData?.dataPoints,
		isLoading: isLoadingBindings || isLoadingCatalogue,
		isSaving: updateFieldBindings.isPending,
		newFieldLabel: tMipManage('master_documents.field_binding.new_field_default'),
		onSave: useCallback(
			(bindings: FieldBindingDto[], onSaved: () => void) => {
				if (!templateId) {
					return;
				}
				// `mutateAsync`, so a caller saving bindings alongside placements can await both
				// and only settle when each has landed. A rejection propagates to that caller;
				// react-query still surfaces it on the mutation as before.
				return updateFieldBindings
					.mutateAsync({ templateId, bindings })
					.then(() => onSaved());
			},
			[templateId, updateFieldBindings]
		),
	});

	const securityScope = useSecurityScope({
		catalogue: catalogueData,
		bindings: state.bindings,
		onChangeBinding: state.onChangeBinding,
	});
	const uncollectedRequirements = useMemo(
		() => catalogueData?.uncollectedRequirements ?? [],
		[catalogueData]
	);

	return { ...state, ...securityScope, uncollectedRequirements };
};
